import { DecimalPipe } from '@angular/common';
import { DestroyRef, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiService, NumberSuffixPipe, skipIfNotVisible } from '@twaice-fe/frontend/shared/services';
import { SystemDetails } from '@twaice-fe/shared/models';
import { keysToCamel } from '@twaice-fe/shared/utilities';
import { BehaviorSubject, catchError, filter, interval, map, of, Subscription, switchMap, tap } from 'rxjs';

interface StorageLiveKpiSummaryRawResponse {
  component_summaries: Record<string, ComponentSummary>; // eslint-disable-line @typescript-eslint/naming-convention
}

interface StorageLiveKpiSummaryResponse {
  componentSummaries: Record<string, ComponentSummary>;
}

export interface KpiValue {
  value: number;
  timestamp: string;
  isSensorReading: boolean;
  standardSensorBk: string | null;
}

export interface ComponentOperatingStateClassification {
  state: string;
  timestamp: string;
  isSensorReading: boolean;
  standardSensorBk: string | null;
}

export interface StorageComponentSummary {
  componentType: 'STORAGE';
  activePower: KpiValue | null;
  socPct: KpiValue | null;
  operatingState: ComponentOperatingStateClassification | null;
}

export type ComponentSummary = StorageComponentSummary;

export interface SystemLiveKpiState {
  activePower: string | null;
  runningState: string | null;
  soc: string | null;
  socPercentage: number | null;
  isLoading: boolean;
}

const SYSTEM_ACTIVE_POWER_SENSOR_BK = 'system_active_power';
const POLLING_INTERVAL_MS = 10_000;

@Injectable()
export class SystemLiveKpisService implements OnDestroy {
  private readonly state$ = new BehaviorSubject<SystemLiveKpiState>({
    activePower: null,
    runningState: null,
    soc: null,
    socPercentage: null,
    isLoading: false,
  });
  readonly kpiState$ = this.state$.asObservable();

  private pollSubscription: Subscription | null = null;
  private system: SystemDetails | null = null;
  private visibleFn: () => boolean = () => false;

  constructor(
    private apiService: ApiService,
    private numberSuffixPipe: NumberSuffixPipe,
    private decimalPipe: DecimalPipe,
    private destroyRef: DestroyRef
  ) {}

  /**
   * Call once from the host component to wire up the system stream and visibility tracking.
   * The service takes over lifecycle management (polling, cleanup) from that point on.
   */
  connect(system$: BehaviorSubject<SystemDetails | null>, visibleFn: () => boolean): void {
    this.visibleFn = visibleFn;

    system$
      .pipe(
        filter((system) => !!system),
        filter((system) => system.systemBk !== this.system?.systemBk),
        takeUntilDestroyed(this.destroyRef),
        tap((system) => {
          this.system = system;
          this.resetState();
          this.patchState({ isLoading: true });
          this.stopPolling();
        }),
        switchMap(() => this.fetchLiveKpis())
      )
      .subscribe(() => {
        this.patchState({ isLoading: false });
        this.startPolling(system$);
      });
  }

  onVisibilityChange(visible: boolean): void {
    if (!visible) {
      this.stopPolling();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private fetchLiveKpis() {
    return this.apiService
      .fetchData<null, StorageLiveKpiSummaryRawResponse>(
        `analytics/overview/customers/${this.system.customerBk}/systems/${this.system.systemBk}/live-kpis`,
        null,
        false
      )
      .pipe(
        map((data) => keysToCamel(data) as StorageLiveKpiSummaryResponse),
        tap((data) => this.processLiveKpiData(data)),
        catchError(() => {
          this.resetState();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      );
  }

  private startPolling(system$: BehaviorSubject<SystemDetails | null>): void {
    if (!this.system) return;

    this.stopPolling();

    this.pollSubscription = system$
      .pipe(
        switchMap(() =>
          interval(POLLING_INTERVAL_MS).pipe(
            takeUntilDestroyed(this.destroyRef),
            filter(() => this.visibleFn() && !!this.system),
            skipIfNotVisible(),
            switchMap(() => {
              if (!this.system) return of(null);
              return this.apiService
                .fetchData<null, StorageLiveKpiSummaryRawResponse>(
                  `analytics/overview/customers/${this.system.customerBk}/systems/${this.system.systemBk}/live-kpis`,
                  null,
                  false
                )
                .pipe(
                  map((data) => keysToCamel(data) as StorageLiveKpiSummaryResponse),
                  tap((data) => this.processLiveKpiData(data)),
                  catchError(() => of(null))
                );
            })
          )
        )
      )
      .subscribe();
  }

  private stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = null;
    }
  }

  private processLiveKpiData(data: StorageLiveKpiSummaryResponse): void {
    const systemComponent = Object.values(data.componentSummaries).find(
      (summary) => summary.activePower?.standardSensorBk === SYSTEM_ACTIVE_POWER_SENSOR_BK
    ) as StorageComponentSummary | undefined;

    if (!systemComponent) return;

    const patch: Partial<SystemLiveKpiState> = {};

    if (systemComponent.activePower) {
      patch.activePower = this.numberSuffixPipe.transform(systemComponent.activePower.value, 1, 'W');
    }

    if (systemComponent.socPct) {
      patch.soc = `${this.decimalPipe.transform(systemComponent.socPct.value, '1.1-1') || '-'} %`;
      patch.socPercentage = systemComponent.socPct.value;
    }

    if (systemComponent.operatingState) {
      const state = systemComponent.operatingState.state;
      patch.runningState = state[0] + state.slice(1).toLowerCase();
    }

    this.patchState(patch);
  }

  private patchState(patch: Partial<SystemLiveKpiState>): void {
    this.state$.next({ ...this.state$.value, ...patch });
  }

  private resetState(): void {
    this.patchState({
      activePower: null,
      runningState: null,
      soc: null,
      socPercentage: null,
    });
  }
}
